import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import secureLocalStorage from 'react-secure-storage';
import AuthChooser from '../../components/auth/AuthChooser';
import {
  SHARE_FUNNEL_EVENT_NAMES,
  readShareFunnelContextFromSearch,
  recordShareFunnelEvent,
} from '../../utils/localOpsShareFunnel';
import { classifyAuthToken } from '../../utils/authTokenClassifier';

const AUTH_BASE = process.env.REACT_APP_AUTH_BASE_URL || '';
const MAGIC_LINK_SUCCESS_DETAIL =
  'Open your email and tap the secure link to continue. It can take a few minutes to arrive. If you do not see it, check Junk/Spam.';
const TASKS_CACHE_KEY = 'localopsTasksCache';
const OFFERINGS_CACHE_KEY = 'localopsOfferingsCache';

const parseShare = (search) => {
  const params = new URLSearchParams(search || '');
  const share = params.get('share');
  return share || null;
};

const parseTarget = (share) => {
  if (!share) return null;
  const trimmed = String(share || '').trim();
  let path = trimmed;
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      path = new URL(trimmed).pathname;
    } catch (err) {
      return null;
    }
  }
  const match = path.match(/^\/share\/(offering|task)\/([^/?#]+)/i);
  if (!match) return null;
  return { type: match[1].toLowerCase(), id: decodeURIComponent(match[2]) };
};

const isSafeLocalOpsReturnTo = (value) => {
  const raw = String(value || '').trim();
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return false;
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return false;
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://www.joegalaxy.net';
    const parsed = new URL(raw, base);
    return parsed.pathname === '/playermap';
  } catch (err) {
    return false;
  }
};

const buildReturnToBridgePath = (returnTo) =>
  `/localops/continue?returnTo=${encodeURIComponent(returnTo)}`;

const SHARE_TARGET_QUERY_KEYS = [
  'shareFunnelId',
  'shareType',
  'shareId',
  'src',
  'source',
  'utm_source',
  'utm_campaign',
  'intent',
  'regionSlug',
  'marketSlug',
];

const buildShareTargetReturnTo = (target, search = '') => {
  if (!target?.type || !target?.id) return null;
  const params = new URLSearchParams();
  params.set('type', target.type);
  params.set('id', target.id);

  const sourceParams = new URLSearchParams(search || '');
  params.set(
    'share',
    sourceParams.get('share') || `/share/${target.type}/${target.id}`,
  );
  SHARE_TARGET_QUERY_KEYS.forEach((key) => {
    const value = sourceParams.get(key);
    if (value) params.set(key, value);
  });

  return `/playermap?${params.toString()}`;
};

const mergeShareTargetReturnTo = (returnTo, target, search = '') => {
  const fallback = buildShareTargetReturnTo(target, search);
  if (!fallback) return returnTo || null;
  if (!returnTo) return fallback;
  try {
    const parsed = new URL(returnTo, 'https://www.joegalaxy.net');
    if (parsed.pathname !== '/playermap') return fallback;
    parsed.searchParams.set('type', target.type);
    parsed.searchParams.set('id', target.id);
    if (!parsed.searchParams.get('share')) {
      parsed.searchParams.set('share', `/share/${target.type}/${target.id}`);
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (err) {
    return fallback;
  }
};

const buildCurrentContinuePath = (location) => {
  const search = removeMagicTokenFromSearch(location?.search || '');
  return `${location?.pathname || '/localops/continue'}${search}${
    location?.hash || ''
  }`;
};

// Normalize continuation target once (only from URL params)
const normalizeReturnTo = (search = '') => {
  const params = new URLSearchParams(search);
  const returnTo = params.get('returnTo');
  const redirect = params.get('redirect');
  const from = params.get('from');
  const candidate = returnTo || redirect || from || null;
  const safeCandidate = isSafeLocalOpsReturnTo(candidate) ? candidate : null;
  if (safeCandidate && typeof window !== 'undefined') {
    try {
      localStorage.setItem('jg:returnTo', safeCandidate);
    } catch (err) {
      // ignore storage failures
    }
  }
  return safeCandidate;
};

const readMagicTokenFromSearch = (search = '') => {
  const params = new URLSearchParams(search || '');
  const token = String(params.get('token') || '').trim();
  return token || null;
};

const removeMagicTokenFromSearch = (search = '') => {
  const params = new URLSearchParams(search || '');
  if (!params.has('token')) return search || '';
  params.delete('token');
  const next = params.toString();
  return next ? `?${next}` : '';
};

const parsePlayerFromPayload = (payload) => {
  const data = payload?.data;
  const playerData =
    data?.player ||
    data?.data?.player ||
    data?.data ||
    payload?.player ||
    payload;
  if (!playerData || typeof playerData !== 'object') return null;
  if (!playerData._id && !playerData.id && !playerData.playerId) return null;
  return playerData;
};

export default function LocalOpsContinue() {
  const location = useLocation();
  const history = useHistory();
  const { player, dispatch } = useContext(AuthContext);
  const normalizedReturnTo = useMemo(
    () => normalizeReturnTo(location.search),
    [location.search],
  );
  const magicTokenFromQuery = useMemo(
    () => readMagicTokenFromSearch(location.search),
    [location.search],
  );
  const share = useMemo(() => parseShare(location.search), [location.search]);
  const shareFunnelContext = useMemo(
    () => readShareFunnelContextFromSearch(location.search),
    [location.search],
  );
  const hasReturnToMode = Boolean(!share && normalizedReturnTo);
  const currentContinuePath = useMemo(
    () => buildCurrentContinuePath(location),
    [location],
  );
  const authReturnTo = useMemo(() => {
    if (share) return currentContinuePath;
    if (normalizedReturnTo) return buildReturnToBridgePath(normalizedReturnTo);
    return '/localops/continue';
  }, [share, currentContinuePath, normalizedReturnTo]);
  const [context, setContext] = useState(null);
  const [snapshotTasks] = useState(() => {
    try {
      const raw = secureLocalStorage.getItem(TASKS_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed?.tasks) ? parsed.tasks : [];
    } catch (err) {
      return [];
    }
  });
  const [snapshotOfferings] = useState(() => {
    try {
      const raw = secureLocalStorage.getItem(OFFERINGS_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed?.offerings) ? parsed.offerings : [];
    } catch (err) {
      return [];
    }
  });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [magicStatus, setMagicStatus] = useState('idle');
  const [primaryBusy, setPrimaryBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [authExpanded, setAuthExpanded] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const target = useMemo(() => parseTarget(share), [share]);
  const shareTargetReturnTo = useMemo(
    () => mergeShareTargetReturnTo(normalizedReturnTo, target, location.search),
    [normalizedReturnTo, target, location.search],
  );
  const resumeSetRef = useRef(false);
  const continueFunnelRecordedRef = useRef(false);
  const tokenBootstrapDoneRef = useRef(false);
  const returnToRedirectedRef = useRef(false);
  const mode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('mode');
  }, [location.search]);

  useEffect(() => {
    if (!shareFunnelContext || continueFunnelRecordedRef.current) return;
    continueFunnelRecordedRef.current = true;
    recordShareFunnelEvent(
      SHARE_FUNNEL_EVENT_NAMES.CONTINUE_STARTED,
      shareFunnelContext,
    );
  }, [shareFunnelContext]);

  const actionableTasks = useMemo(() => {
    const list = Array.isArray(snapshotTasks) ? snapshotTasks : [];
    return list.filter((task) => {
      const status = String(task?.status || '').toLowerCase();
      return !['completed', 'cancelled', 'canceled', 'expired'].includes(status);
    });
  }, [snapshotTasks]);

  const availableOfferings = useMemo(() => {
    const list = Array.isArray(snapshotOfferings) ? snapshotOfferings : [];
    return list.filter((offering) => {
      const status = String(offering?.status || '').toLowerCase();
      const quantities = [
        offering?.inventory,
        offering?.availableQuantity,
        offering?.quantityAvailable,
        offering?.unitsAvailable,
        offering?.stock,
        offering?.quantity,
      ].filter((v) => v !== null && v !== undefined);
      const minQty =
        quantities.length > 0
          ? Math.min(...quantities.map((n) => Number(n)))
          : null;
      const soldOut = minQty !== null && Number(minQty) <= 0;
      const availableQuantity = Number(offering?.availableQuantity);
      if (
        process.env.NODE_ENV !== 'production' &&
        status === 'available' &&
        Number.isFinite(availableQuantity) &&
        availableQuantity > 0 &&
        soldOut
      ) {
        console.assert(
          false,
          '[localops][continue] available offering excluded despite stock',
          {
            availableQuantity,
            status,
            soldOut,
            quantities,
          },
        );
      }
      return status === 'available' && !soldOut;
    });
  }, [context]);

  const formatAmount = (amount) => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) return '$—';
    return `$${parsed.toFixed(2)}`;
  };

  const bestTask = actionableTasks[0] || null;
  const bestOffering = availableOfferings[0] || null;
  const completionPlan = useMemo(() => {
    if (mode === 'completed_task') {
      if (bestTask) {
        const label = formatAmount(
          bestTask.price ||
            bestTask.amount ||
            bestTask.amountUsd ||
            bestTask.priceUsd,
        );
        const taskId =
          bestTask.taskId || bestTask.id || bestTask._id || target?.id || '';
        return {
          copy: 'That task is already complete. Another local task is available to review.',
          cta: `View ${label} task`,
          onClick: () =>
            history.push(`/share/task/${encodeURIComponent(String(taskId))}`),
        };
      }
      return {
        copy:
          'That task is already complete. You can post a local task or check the map for open work.',
        cta: 'Post a task',
        onClick: () => history.push('/localops/create?type=task'),
        secondaryCta: 'Check local tasks',
        secondaryOnClick: () => history.push('/playermap'),
      };
    }

    if (mode === 'completed_offering') {
      if (bestOffering) {
        const offeringId =
          bestOffering.offeringId ||
          bestOffering.id ||
          bestOffering._id ||
          target?.id ||
          '';
        return {
          copy:
            'That item is no longer available. Here’s something else happening locally.',
          cta: 'View available offerings',
          onClick: () =>
            history.push(
              `/share/offering/${encodeURIComponent(String(offeringId))}`,
            ),
        };
      }
      return {
        copy: 'That item is no longer available. Share something local instead.',
        cta: 'Post an offering',
        onClick: () => history.push('/localops/create?type=offering'),
        secondaryCta: 'Earn locally',
        secondaryOnClick: () => history.push('/playermap'),
      };
    }

    return null;
  }, [mode, bestTask, bestOffering, history, target]);

  // Persist LocalOps continuation intent (ResumeGate executes it)
  if (share && !player?.token && !resumeSetRef.current) {
    const resumePayload = {
      type: 'localops',
      share,
      source: 'localops_continue',
      ...(shareFunnelContext || {}),
    };
    try {
      localStorage.setItem('jg:resume', JSON.stringify(resumePayload));
      console.log('[LocalOpsContinue] wrote jg:resume', resumePayload);
      resumeSetRef.current = true;
    } catch (err) {
      // ignore storage failures
    }
  }

  useEffect(() => {
    console.log('[SPA] LocalOpsContinue rendered — no network navigation', {
      location: window.location.href,
      player: localStorage.getItem('player'),
    });
  }, []);

  // Split-hosting bootstrap: consume token on SPA origin, hydrate auth, and strip token from URL.
  useEffect(() => {
    if (!magicTokenFromQuery) return;

    const finalizeUrl = () => {
      // URL hygiene only: strip token from address bar without triggering a route navigation
      // that could race with ResumeGate's redirect to /playermap.
      if (typeof window === 'undefined') return;
      const cleanedSearch = removeMagicTokenFromSearch(location.search);
      if (cleanedSearch === location.search) return;
      const nextUrl = `${location.pathname}${cleanedSearch}${location.hash || ''}`;
      window.history.replaceState(window.history.state, '', nextUrl);
    };

    if (player?.token) {
      finalizeUrl();
      return;
    }
    if (tokenBootstrapDoneRef.current) return;
    tokenBootstrapDoneRef.current = true;

    let cancelled = false;
    const tokenClass = classifyAuthToken(magicTokenFromQuery);
    if (tokenClass.type === 'magic-link-request') {
      setError(
        'This sign-in link expired or needs to be reopened from your email. Request a fresh link to continue.',
      );
      finalizeUrl();
      return undefined;
    }
    if (tokenClass.type !== 'session') {
      setError(
        'This sign-in link is invalid or expired. Request a fresh link to continue.',
      );
      finalizeUrl();
      return undefined;
    }

    const headers = { Authorization: `Bearer ${magicTokenFromQuery}` };

    const run = async () => {
      try {
        const res = await axiosConn.get('players/me', {
          headers,
          skipAuthExpiredRecovery: true,
        });
        const hydratedPlayer = parsePlayerFromPayload(res?.data);
        if (!hydratedPlayer) {
          throw new Error('missing-player-payload');
        }
        const authPayload = {
          status: 'success',
          token: magicTokenFromQuery,
          data: { player: hydratedPlayer },
          // Mark source so nav can apply magic-link-specific UX safely.
          authMethod: 'magic_link',
        };
        localStorage.setItem('player', JSON.stringify(authPayload));
        if (cancelled) return;
        dispatch({ type: 'LOGIN_SUCCESS', payload: authPayload });
        console.log('[CONTINUE] hydrated player from magic token');
      } catch (err) {
        if (cancelled) return;
        console.error('[CONTINUE] token bootstrap failed', {
          message: err?.message,
          status: err?.response?.status,
        });
        setError('This sign-in link is invalid or expired. Request a new link to continue.');
      } finally {
        if (!cancelled) finalizeUrl();
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    magicTokenFromQuery,
    player?.token,
    dispatch,
    history,
    location.pathname,
    location.search,
    location.hash,
  ]);

  // Hydrate auth state from localStorage/cookie if missing
  useEffect(() => {
    if (player) return;
    let stored = localStorage.getItem('player');
    if (!stored || stored === 'null') {
      const match = document.cookie.match(/(?:^|; )player=([^;]+)/);
      if (match && match[1]) {
        try {
          stored = decodeURIComponent(match[1]);
          localStorage.setItem('player', stored);
          console.log('[CONTINUE] restored player from cookie');
        } catch (e) {
          console.error('[CONTINUE] cookie decode failed', e);
        }
      }
    }
    try {
      const parsed = stored ? JSON.parse(stored) : null;
      if (parsed && parsed.token) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: parsed });
        console.log('[CONTINUE] hydrated player from storage');
      }
    } catch (e) {
      console.error('[CONTINUE] parse failed', e);
    }
  }, [player, dispatch]);

  // Fetch continuation context (deferred to next tick)
  useEffect(() => {
    console.log('[CONTINUE] useEffect fired', { share });

    if (!share) {
      console.log('[CONTINUE] no share param yet, skipping fetch');
      return undefined;
    }

    const id = setTimeout(() => {
      console.log('[CONTINUE] deferred fetch start', {
        path: window.location.pathname,
        href: window.location.href,
        share,
      });

      setStatus('loading');

      axiosConn
        .get('continuation/context', { params: { share } })
        .then((res) => {
          console.log('[CONTINUE] context fetch success', res?.data);
          setContext(res?.data?.data || null);
          setStatus('ready');
        })
        .catch((err) => {
          console.error('[CONTINUE] context fetch failed', {
            message: err?.message,
            response: err?.response,
          });
          setContext(null);
          setStatus('error');
          setError('We could not load this link. Try again.');
        });
    }, 0);

    return () => {
      console.log('[CONTINUE] cleanup, clearing timeout');
      clearTimeout(id);
    };
  }, [share]);

  useEffect(() => {
    if (!share || mode || !player?.token || !shareTargetReturnTo) return;
    if (returnToRedirectedRef.current) return;
    returnToRedirectedRef.current = true;
    try {
      localStorage.setItem('jg:returnTo', shareTargetReturnTo);
      localStorage.removeItem('jg:resume');
    } catch (err) {
      // ignore storage failures
    }
    history.replace(
      shareTargetReturnTo,
      shareFunnelContext ? { shareFunnelContext } : undefined,
    );
  }, [
    history,
    mode,
    player?.token,
    share,
    shareFunnelContext,
    shareTargetReturnTo,
  ]);

  useEffect(() => {
    if (share || !normalizedReturnTo || !player?.token) return;
    if (returnToRedirectedRef.current) return;
    returnToRedirectedRef.current = true;
    history.replace(normalizedReturnTo);
  }, [share, normalizedReturnTo, player?.token, history]);

  if (!share && !hasReturnToMode) {
    return (
      <div className="localops-continue">
        <div className="localops-continue__card">
          <h2>JG LocalOps link unavailable</h2>
          <p>This link is missing the details needed to continue.</p>
        </div>
      </div>
    );
  }

  if (player && completionPlan) {
    return (
      <div className="localops-continue">
        <div className="localops-continue__card">
          <h2>Keep moving in JG LocalOps</h2>
          <p>{completionPlan.copy}</p>
          <div style={{ color: '#9fb6d1', marginBottom: 10 }}>
            Verified local work can build trust and create real value.
          </div>
          <button
            type="button"
            className="localops-btn"
            onClick={completionPlan.onClick}
          >
            {completionPlan.cta}
          </button>
          {completionPlan.secondaryCta && completionPlan.secondaryOnClick ? (
            <button
              type="button"
              className="localops-btn"
              onClick={completionPlan.secondaryOnClick}
              style={{
                marginTop: 10,
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.4)',
              }}
            >
              {completionPlan.secondaryCta}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (player) {
    return (
      <div className="localops-continue">
        <div className="localops-continue__card">
          <h2>Continuing your JG LocalOps action</h2>
          <p>We’re resuming where you left off.</p>
          <div style={{ color: '#9fb6d1', marginBottom: 10 }}>
            Verified local work can build trust and create real value.
          </div>
          {error ? (
            <div className="localops-continue__error">{error}</div>
          ) : (
            <div className="localops-helper">Working on it…</div>
          )}
        </div>
      </div>
    );
  }

  // Unauthed: action-first with inline auth expansion
  const returnTo = authReturnTo;
  const loginUrl = `/login?returnTo=${encodeURIComponent(returnTo)}`;
  const registerUrl = `/register?returnTo=${encodeURIComponent(
    returnTo,
  )}&source=localops_continue&bonus=jgk_100`;
  // OAuth gated; coming soon.
  // const googleUrl = `${AUTH_BASE}/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
  // const facebookUrl = `${AUTH_BASE}/auth/facebook?returnTo=${encodeURIComponent(returnTo)}`;
  // const appleUrl = `${AUTH_BASE}/auth/apple?returnTo=${encodeURIComponent(returnTo)}`;

  const validateEmail = (value) => {
    if (!value) return 'Email is required.';
    const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!re.test(value.trim())) return 'Enter a valid email address.';
    return '';
  };

  const handleMagicLink = async () => {
    const validation = validateEmail(email);
    setEmailError(validation);
    if (validation) return;
    setMagicStatus('sending');
    setError('');
    try {
      const magicUrl = AUTH_BASE ? `${AUTH_BASE}/auth/magic` : '/auth/magic';
      const res = await fetch(magicUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email, returnTo }),
      });
      if (!res.ok) {
        throw new Error('magic-link-failed');
      }
      setMagicStatus('sent');
      setEmail(email.trim());
    } catch (err) {
      setMagicStatus('error');
      setError('Could not send magic link. Please try again.');
    }
  };

  return (
    <div
      className="localops-continue"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#050912',
        padding: '32px 16px',
      }}
    >
      <style>
        {`
        .loc-btn {
          min-height: 48px;
          transition: transform 0.16s ease-out, box-shadow 0.16s ease-out, border-color 0.16s ease-out;
        }
        .loc-btn:active {
          transform: scale(0.985);
          box-shadow: 0 6px 18px rgba(0,0,0,0.25);
        }
        .loc-btn.secondary {
          border: 1px solid rgba(255,255,255,0.5);
        }
        .loc-btn.secondary:hover {
          border-color: rgba(255,255,255,0.65);
        }
        .continue-header {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: #d8e8ff;
          margin-bottom: 12px;
          font-weight: 600;
          letter-spacing: -0.01em;
          opacity: 0.9;
          line-height: 1;
        }
        .jg-logo-inline {
          height: 1.35em;
          width: auto;
          flex-shrink: 0;
          border-radius: 50%;
        }
        .continue-header-text {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #d8e8ff;
          font-weight: 600;
          letter-spacing: -0.01em;
        }
        .continue-header-text .dot {
          opacity: 0.65;
        }
        `}
      </style>
      <div
        className="localops-continue__card"
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'rgba(12,16,28,0.95)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 20,
          padding: '20px 22px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.35)',
        }}
      >
        <div className="continue-header">
          <img
            className="jg-logo-inline"
            src="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png"
            alt="JG"
          />
          <span className="continue-header-text">
            <span>JG LocalOps</span>
            <span className="dot">•</span>
            <span>Real-World Action</span>
          </span>
        </div>
          <h2 style={{ margin: '0 0 6px', color: '#e8edf7' }}>
            {completionPlan ? 'Keep moving' : 'Continue your JG LocalOps action'}
          </h2>
          <p style={{ margin: '0 0 10px', color: '#b4c3dc', lineHeight: 1.5 }}>
            {completionPlan
              ? completionPlan.copy
              : "Sign in once. We'll bring you back to this JG LocalOps action."}
          </p>
          <div style={{ color: '#9fb6d1', margin: '0 0 12px', fontSize: 13 }}>
            Verified local work can build trust and create real value.
          </div>
          {error && (
            <div className="localops-continue__error">{error}</div>
          )}
          <div className="localops-continue__actions">
            {completionPlan ? (
              <>
                <button
                  type="button"
                  className="loc-btn"
                  disabled={
                    primaryBusy || authRequired || !completionPlan.onClick
                  }
                  onClick={() => {
                    if (!completionPlan.onClick) return;
                    if (player) {
                      setPrimaryBusy(true);
                      completionPlan.onClick();
                      return;
                    }
                    setAuthRequired(true);
                    setAuthExpanded(true);
                  }}
                  style={{
                    width: '100%',
                    marginBottom: 10,
                    padding: '12px 14px',
                    borderRadius: 12,
                    background:
                      'linear-gradient(135deg, rgba(25,147,255,0.9), rgba(79,106,255,0.9))',
                    color: '#fff',
                    fontWeight: 800,
                    border: 'none',
                    opacity: authRequired ? 0.72 : 1,
                    cursor: authRequired ? 'not-allowed' : 'pointer',
                    transition: 'opacity 0.15s ease',
                    boxShadow: authRequired ? 'none' : undefined,
                  }}
                >
                  {completionPlan.cta}
                </button>
                {completionPlan.secondaryCta ? (
                  <button
                    type="button"
                    className="loc-btn secondary localops-btn"
                    disabled={
                      primaryBusy || authRequired || !completionPlan.secondaryOnClick
                    }
                    onClick={() => {
                      if (!completionPlan.secondaryOnClick) return;
                      if (player) {
                        setPrimaryBusy(true);
                        completionPlan.secondaryOnClick();
                        return;
                      }
                      setAuthRequired(true);
                      setAuthExpanded(true);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      borderRadius: 12,
                      background: 'transparent',
                      color: '#dbe8ff',
                      fontWeight: 700,
                      opacity: authRequired ? 0.72 : 1,
                      cursor: authRequired ? 'not-allowed' : 'pointer',
                      transition: 'opacity 0.15s ease',
                      boxShadow: 'none',
                    }}
                  >
                    {completionPlan.secondaryCta}
                  </button>
                ) : null}
              </>
            ) : null}

            {(authExpanded || !completionPlan) && (
              <div style={{ marginTop: 12 }}>
                <AuthChooser
                  primaryAuthMethod="magic_link"
                  headerText={authExpanded ? 'Continue to JG LocalOps' : undefined}
                  subText={
                    authExpanded
                      ? "Use email to continue this LocalOps action."
                      : undefined
                  }
                  onPrimaryStart={() => setPrimaryBusy(true)}
                  onPrimary={() => {
                    try {
                      localStorage.setItem('localops:signup_bonus_intent', 'JGK_100');
                    } catch (err) {
                      // ignore
                    }
                    history.replace(registerUrl);
                  }}
                  primaryDisabled={primaryBusy}
                  email={email}
                  onEmailChange={setEmail}
                  emailError={emailError}
                  onEmailErrorClear={() => setEmailError('')}
                  onSendMagicLink={handleMagicLink}
                  magicStatus={magicStatus}
                />
              </div>
            )}
          {/* <a className="localops-btn localops-btn--ghost" href={googleUrl}>
            Continue with Google
          </a>
          <a className="localops-btn localops-btn--ghost" href={facebookUrl}>
            Continue with Facebook
          </a>
          <a className="localops-btn localops-btn--ghost" href={appleUrl}>
            Continue with Apple
          </a> */}
        </div>
        <div
          className="localops-helper"
          style={{ marginTop: 12, color: '#9fb6d1', lineHeight: 1.4 }}
        >
          {status === 'ready' || hasReturnToMode
            ? "We'll bring you back to your JG LocalOps action after sign-in."
            : 'Loading share details…'}
          {status === 'ready' || hasReturnToMode ? (
            <div style={{ marginTop: 6, opacity: 0.8 }}>
              No password required • Link expires soon • No spam
            </div>
          ) : null}
          {magicStatus === 'sent' ? (
            <div
              role="status"
              style={{
                marginTop: 10,
                display: 'grid',
                gap: 4,
                border: '1px solid rgba(74,222,128,0.34)',
                borderRadius: 14,
                background: 'rgba(22,163,74,0.13)',
                boxShadow: '0 14px 28px rgba(22,163,74,0.12)',
                padding: '12px 14px',
                color: '#c8f7d5',
                lineHeight: 1.45,
              }}
            >
              <strong style={{ color: '#f3fff7', fontSize: 15, lineHeight: 1.25 }}>
                Magic link sent.
              </strong>
              <span style={{ fontWeight: 650 }}>{MAGIC_LINK_SUCCESS_DETAIL}</span>
            </div>
          ) : null}
          {magicStatus === 'sending' ? (
            <div style={{ marginTop: 6, color: '#b4c3dc' }}>Continuing…</div>
          ) : null}
          <div style={{ marginTop: 10, fontSize: 12 }}>
            {completionPlan
              ? 'No dead ends. This link moves you to the next best LocalOps action.'
              : 'No onboarding. No spam. We only use your account to verify real actions.'}
          </div>
        </div>
      </div>
    </div>
  );
}
